import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AuditFeedResponse, AuditFeedRow, AuditFilterOptions, AuditSnapshot } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  EmptyRow, ErrorBanner, ExportMenu, SortableHeader, downloadCsv, formatDateTime, today, useTableSort,
} from '../components/ui';

const PAGE = 50;

/** The words the app uses for each audited thing, rather than its table name. */
const ENTITY_LABEL: Record<string, string> = {
  DailyRecord: 'Daily record',
  IncidentEntry: 'Incident / reward',
  PointsKeyEntry: 'Points Key entry',
  Schedule: 'Schedule',
  Employee: 'Employee',
  User: 'Person with access',
  UserRoleAssignment: 'Role assignment',
  Role: 'Role',
  RoleCapability: 'Role permission',
  Facility: 'Facility',
  Period: 'Period',
  PayCode: 'Pay code',
  Settings: 'Setting',
  ImportBatch: 'Import',
};

/** Access changes are the reason this screen exists, so they read differently. */
const ACCESS_ENTITIES = new Set(['User', 'UserRoleAssignment', 'Role', 'RoleCapability']);

const ACTION_COLOR: Record<string, string> = {
  create: 'var(--green)',
  approve: 'var(--green)',
  update: 'var(--blue)',
  import: 'var(--blue)',
  reject: 'var(--orange)',
  lock: 'var(--orange)',
  delete: 'var(--red)',
};

/**
 * Every change, newest first.
 *
 * Per-record history answers "how did this row get this way"; this answers
 * "what happened, and who did it". For access changes it is the only answer
 * available — admin.Users and admin.UserRoleAssignments are audited on every write
 * but have no record screen to hang a history panel off.
 */
export function AuditLog() {
  const { can } = useApp();
  const canView = can('audit', 'view');

  const [entity, setEntity] = useState('all');
  const [action, setAction] = useState('all');
  const [changedBy, setChangedBy] = useState('all');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [take, setTake] = useState(PAGE);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const filters = { entity, action, changedBy, search, from, to };

  const feed = useQuery({
    queryKey: ['audit', filters, take],
    queryFn: () => api.get<AuditFeedResponse>('/api/audit', { ...filters, take }),
    enabled: canView,
  });

  const options = useQuery({
    queryKey: ['audit-filters'],
    queryFn: () => api.get<AuditFilterOptions>('/api/audit/filters'),
    enabled: canView,
  });

  // Reset the page size whenever the question changes, or a narrowed filter
  // would silently keep asking for 200 rows it no longer needs.
  const narrow = (set: (v: string) => void) => (value: string) => {
    set(value);
    setTake(PAGE);
    setOpenRow(null);
  };

  // Sorting reorders the entries already loaded; the feed itself always arrives newest first.
  const { rows, sort, onSort } = useTableSort(feed.data?.rows ?? [], {
    when: (r) => r.changedAtUtc,
    person: (r) => r.changedBy,
    action: (r) => r.action,
    record: (r) => `${ENTITY_LABEL[r.entityName] ?? r.entityName} ${r.entityKey}`,
    change: (r) => r.summary,
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Audit Log</div>
          <div className="page-subtitle">
            Every change · newest first · who, when, and the record before and after
          </div>
        </div>
        <ExportMenu
          onExport={() =>
            downloadCsv(
              `audit-log-${today()}.csv`,
              ['When (UTC)', 'Person', 'Action', 'Record type', 'Record', 'Summary', 'Version before', 'Version after'],
              rows.map((r) => [
                r.changedAtUtc, r.changedBy, r.action,
                ENTITY_LABEL[r.entityName] ?? r.entityName, r.entityKey,
                r.summary ?? '', r.versionBefore ?? '', r.versionAfter ?? '',
              ]),
            )
          }
        />
      </div>

      <div className="filter-row">
        <select className="control" style={{ maxWidth: 210 }} value={entity}
          onChange={(e) => narrow(setEntity)(e.target.value)} aria-label="Record type">
          <option value="all">All record types</option>
          {(options.data?.entities ?? []).map((e) => (
            <option key={e} value={e}>{ENTITY_LABEL[e] ?? e}</option>
          ))}
        </select>

        <select className="control" value={action}
          onChange={(e) => narrow(setAction)(e.target.value)} aria-label="Action">
          <option value="all">All actions</option>
          {(options.data?.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <select className="control" style={{ maxWidth: 220 }} value={changedBy}
          onChange={(e) => narrow(setChangedBy)(e.target.value)} aria-label="Person">
          <option value="all">Anyone</option>
          {(options.data?.people ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" className="control" value={from}
            onChange={(e) => narrow(setFrom)(e.target.value)} aria-label="From" />
          <span className="meta">to</span>
          <input type="date" className="control" value={to}
            onChange={(e) => narrow(setTo)(e.target.value)} aria-label="To" />
        </div>

        <input className="control" style={{ width: 210 }} placeholder="Search record or summary…"
          value={search} onChange={(e) => narrow(setSearch)(e.target.value)} />
      </div>

      <div className="meta" style={{ marginBottom: 8 }}>
        {feed.isLoading ? 'Reading the audit log…' : `${rows.length} entries${feed.data?.hasMore ? ' — more beyond these' : ''}`}
        {' · '}times are UTC
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="WHEN" column="when" sort={sort} onSort={onSort} />
              <SortableHeader label="PERSON" column="person" sort={sort} onSort={onSort} />
              <SortableHeader label="ACTION" column="action" sort={sort} onSort={onSort} />
              <SortableHeader label="RECORD" column="record" sort={sort} onSort={onSort} />
              <SortableHeader label="WHAT CHANGED" column="change" sort={sort} onSort={onSort} />
              <th />
            </tr>
          </thead>
          <tbody>
            {feed.isLoading && <EmptyRow colSpan={6}>Reading the audit log…</EmptyRow>}
            {!feed.isLoading && rows.length === 0 && (
              <EmptyRow colSpan={6}>No changes match these filters.</EmptyRow>
            )}
            {rows.map((r) => {
              const key = `${r.source}:${r.auditId}`;
              return (
                <Fragment key={key}>
                  <tr>
                    <td className="meta" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.changedAtUtc)}</td>
                    <td className="strong">{r.changedBy}</td>
                    <td>
                      <span style={{ color: ACTION_COLOR[r.action] ?? 'var(--text-secondary)', fontWeight: 600 }}>
                        {r.action}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: ACCESS_ENTITIES.has(r.entityName) ? 'var(--orange-text)' : 'var(--text)' }}>
                        {ENTITY_LABEL[r.entityName] ?? r.entityName}
                      </div>
                      <div className="meta">{r.entityKey}</div>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {r.summary ?? '—'}
                      {r.versionBefore != null && r.versionAfter != null && (
                        <span className="meta"> · v{r.versionBefore} → v{r.versionAfter}</span>
                      )}
                    </td>
                    <td className="num">
                      {r.hasSnapshot && (
                        <button
                          className="btn btn--sm"
                          onClick={() => setOpenRow(openRow === key ? null : key)}
                        >
                          {openRow === key ? 'Hide' : 'Snapshot'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {openRow === key && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--surface-muted)' }}>
                        <Snapshot row={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {feed.data?.hasMore && (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn--sm" onClick={() => setTake((t) => Math.min(t + PAGE, 200))}>
            Show more
          </button>
          <span className="meta" style={{ marginLeft: 10 }}>
            Showing the most recent {rows.length}. Narrow the dates or the record type to reach further back.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * The stored before/after. Fetched only when opened: these are whole-record
 * snapshots and they carry personal data, so they do not travel with the feed.
 */
function Snapshot({ row }: { row: AuditFeedRow }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-snapshot', row.source, row.auditId],
    queryFn: () => api.get<AuditSnapshot>(`/api/audit/${row.source}/${row.auditId}`),
  });

  if (isLoading) return <div className="meta" style={{ padding: '4px 0' }}>Loading the snapshot…</div>;
  if (error) return <ErrorBanner message={(error as Error).message} />;

  return (
    <div style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr 1fr', padding: '6px 0' }}>
      <Pane title="BEFORE" json={data?.beforeJson} empty="Nothing — this record was created here." />
      <Pane title="AFTER" json={data?.afterJson} empty="Nothing — this record was deleted here." />
    </div>
  );
}

function Pane({ title, json, empty }: { title: string; json?: string | null; empty: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.3, marginBottom: 5 }}>
        {title}
      </div>
      {!json && <div className="meta">{empty}</div>}
      {json && (
        <pre
          style={{
            margin: 0, fontSize: 11.5, lineHeight: 1.55, maxHeight: 260, overflow: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            padding: '10px 12px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}
        >
          {pretty(json)}
        </pre>
      )}
    </div>
  );
}

/** Stored compact to keep the log small; expanded here for reading. */
function pretty(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
